import { BaseItem } from '@algolia/autocomplete-core';
import { AutocompleteQuerySuggestionsHit } from '@algolia/autocomplete-plugin-query-suggestions/dist/esm/types';
import algoliasearch from 'algoliasearch/lite';
import instantsearch from 'instantsearch.js';
import { connectAutocomplete } from 'instantsearch.js/es/connectors';
import connectSearchBox from 'instantsearch.js/es/connectors/search-box/connectSearchBox';
import { configure } from 'instantsearch.js/es/widgets';

import { autocompleteSearch } from '@/src/utilities/search/searchRenderFunctions';
import { generateSearchResultsPath } from '@/src/utilities/search/utils';

export async function loadProviderLandingSearch(): Promise<void> {
  const config = window['provider-landing-search'];

  if (!config) {
    return;
  }

  const { searchIndexName, applicationId, searchKey, searchContext } = config;

  const { searchTerm: query } = searchContext;

  const algoliaClient = algoliasearch(applicationId, searchKey);

  const searchClient = {
    ...algoliaClient,
    search(requests: any[]) {
      return algoliaClient.search(requests);
    },
  };

  const search = instantsearch({
    indexName: searchIndexName,
    initialUiState: {
      [searchIndexName]: {
        query,
      },
    },
    searchClient,
    insights: true,
    routing: true,
  });

  let userLocation: { lat: number; lng: number } | null = null;

  const geocoder = new google.maps.Geocoder();

  // render functions:
  const renderAutocomplete = (
    renderOptions: {
      indices: object[];
      currentRefinement: string;
      refine: any; // should be a function
      widgetParams: any; // should be an object
    },
    isFirstRender: boolean
  ): void => {
    const { widgetParams } = renderOptions;

    if (isFirstRender) {
      const aaForm = document.createElement('form'); // aa-Form

      const aaPanel = document.createElement('div'); // dropdown panel

      const suggestionsList = document.createElement('div'); // contains 1 li per hit/suggestion
      const resultsList = document.createElement('div'); // contains 1 li per hit/suggestion
      suggestionsList.classList.add('suggestions-list');
      resultsList.classList.add('results-list');
      aaPanel.appendChild(suggestionsList);
      aaPanel.appendChild(resultsList);

      const aaInputWrapperPrefix = document.createElement('div'); // aa-InputWrapperPrefix
      const aaLabel = document.createElement('label'); // aa-Label

      const aaExtendedSearchbox = document.createElement('div'); // aa-ExtendedSearchbox
      const aaClearButton = document.createElement('button'); // aa-ClearButton
      aaClearButton.setAttribute('aria-label', 'Clear search form');

      const aaSubmitButton = document.createElement(
        'button'
      ) as HTMLButtonElement; // aa-SubmitButton

      const aaLoadingIndicator = document.createElement('div'); // aa-LoadingIndicator
      const aaLoadingIcon = document.createElement('svg'); // aa-LoadingIcon

      const aaInputWrapper = document.createElement('div'); // aa-InputWrapper
      const aaInput = document.createElement('input'); // aa-Input

      const aaExtendedGeobox = document.createElement('div');
      const geoClearButton = document.createElement('button');
      geoClearButton.setAttribute('aria-label', 'Clear city or zip code input');

      const geocodeLabel = document.createElement('label'); // geocode label
      const geocodeInput = document.createElement('input'); // geocode input
      const currentLocationContainer = document.createElement('div');
      const iconContainer = document.createElement('div');
      const currentLocationButton = document.createElement('button'); // current location button

      const aaInputWrapperSuffix = document.createElement('div'); // aa-InputWrapperSuffix

      autocompleteSearch(query);
      aaInput.addEventListener('focus', (event) => {
        const eventTarget = event.target as HTMLInputElement;

        if (eventTarget.value.length > 0) {
          aaClearButton.classList.remove('hidden');
        }

        if (eventTarget.value.length > 1) {
          aaPanel.classList.remove('hidden');
        }
      });

      aaInput.addEventListener('click', (event) => {
        const eventTarget = event.target as HTMLInputElement;
        if (eventTarget.value.length > 1) {
          aaPanel.classList.remove('hidden');
        }
      });

      aaInput.addEventListener('input', (event) => {
        const eventTarget = event.target as HTMLInputElement;
        if (eventTarget.value.length > 1) {
          autocompleteSearch(eventTarget.value);
          aaPanel.classList.remove('hidden');
        } else {
          aaPanel.classList.add('hidden');
        }

        if (aaInput.value.length > 0) {
          aaClearButton.classList.remove('hidden');
        } else {
          aaClearButton.classList.add('hidden');
        }
      });

      let panelIsFocused = false;
      aaPanel.addEventListener('mousedown', (event) => {
        event.preventDefault();

        panelIsFocused = true;
      });

      aaClearButton.addEventListener('mousedown', (event) => {
        event.preventDefault();
        aaPanel.classList.add('hidden');
      });

      aaInput.addEventListener('blur', () => {
        searchContext.searchTerm = aaInput.value;
        autocompleteSearch(aaInput.value);

        if (!panelIsFocused) {
          aaPanel.classList.add('hidden');
        }
      });

      // add hover state on mouse enter
      // Add a single event listener to the parent element
      aaPanel.addEventListener(
        'mouseenter',
        (event) => {
          const target = event.target as HTMLElement;

          if (target.tagName === 'A') {
            target.classList.add('bg-stone-100', 'hover:underline');
          }
        },
        true
      ); // Use capture phase to ensure it catches the event

      // remove background color on mouse leave UNLESS it's the currently focused element
      // Add a single event listener to the parent element
      aaPanel.addEventListener(
        'mouseleave',
        (event) => {
          const target = event.target as HTMLElement;
          const currentFocus = document.querySelector('#selected-query');

          if (target.tagName === 'A') {
            if (target !== currentFocus) {
              target.classList.remove('bg-stone-100');
            }
          }
        },
        true
      ); // Use capture phase to ensure it catches the event

      // arrow key navigation
      aaInput.addEventListener('keydown', (event: KeyboardEvent) => {
        const focusableEls = Array.prototype.slice.call(
          aaPanel.querySelectorAll('a')
        );
        const lastFocusableEl = focusableEls[focusableEls.length - 1];

        const currentFocus = document.querySelector('#selected-query');
        let currentIndex = focusableEls.indexOf(currentFocus);
        const nextFocusableEl = focusableEls[currentIndex + 1];
        const prevFocusableEl = focusableEls[currentIndex - 1];

        if (event.key === 'ArrowDown' && aaInput.value.length > 1) {
          event.preventDefault();

          if (currentIndex === -1) {
            searchContext.searchTerm = aaInput.value;
          }

          aaPanel.classList.remove('hidden');

          currentFocus?.removeAttribute('id');

          // background color is not removed if the element is currently hovered
          if (!currentFocus?.matches(':hover')) {
            currentFocus?.classList.remove('bg-stone-100');
          }

          currentIndex++;

          if (!nextFocusableEl) {
            currentFocus?.removeAttribute('id');
            currentFocus?.classList.remove('bg-stone-100');

            aaPanel.scroll({ top: 0, behavior: 'smooth' });

            aaInput.value = searchContext.searchTerm;
          } else {
            nextFocusableEl.classList.add('bg-stone-100');

            nextFocusableEl.setAttribute('id', 'selected-query');

            nextFocusableEl.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
              inline: 'nearest',
            });

            aaInput.value = nextFocusableEl.textContent.trim();
          }
        }

        if (event.key === 'ArrowUp' && aaInput.value.length > 1) {
          event.preventDefault();

          if (currentIndex === -1) {
            searchContext.searchTerm = aaInput.value;
          }

          aaPanel.classList.remove('hidden');

          currentFocus?.removeAttribute('id');

          // background color is not removed if the element is currently hovered
          if (!currentFocus?.matches(':hover')) {
            currentFocus?.classList.remove('bg-stone-100');
          }

          currentIndex--;

          if (!prevFocusableEl) {
            currentFocus?.removeAttribute('id');
            currentFocus?.classList.remove('bg-stone-100');

            if (!currentFocus) {
              lastFocusableEl.classList.add('bg-stone-100');

              lastFocusableEl.setAttribute('id', 'selected-query');
              lastFocusableEl.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'nearest',
              });

              aaInput.value = lastFocusableEl.textContent.trim();
            } else {
              aaInput.value = searchContext.searchTerm;
            }
          } else {
            prevFocusableEl.classList.add('bg-stone-100');

            prevFocusableEl.setAttribute('id', 'selected-query');
            prevFocusableEl.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
              inline: 'nearest',
            });

            aaInput.value = prevFocusableEl.textContent.trim();
          }
        }

        if (event.key === 'Enter') {
          event.preventDefault();

          if (currentFocus?.hasAttribute('data-app-autocomplete-result')) {
            const url = currentFocus.getAttribute('href') as string;
            window.location.href = url;
          } else {
            if (currentFocus) {
              const selectedQuery = currentFocus.textContent?.trim() as string;
              aaInput.value = selectedQuery;
            }
            const query = aaInput.value;
            const zipcode =
              geocodeInput.value.length > 0 ? geocodeInput.value : '';

            const url = generateSearchResultsPath(query, zipcode).toString();

            window.location.href = url;
          }
        }
      });

      aaClearButton.addEventListener('click', (event) => {
        event.preventDefault();
        aaInput.value = '';
        aaClearButton.classList.add('hidden');
      });

      geocodeInput.addEventListener('keydown', (event: KeyboardEvent) => {
        if (event.key === 'Enter') {
          const query = aaInput.value;
          const zipcode = geocodeInput.value;
          const url = generateSearchResultsPath(query, zipcode).toString();

          if (zipcode.length > 0 || query.length > 0) {
            window.location.href = url;
          }
        }
      });

      geoClearButton.addEventListener('click', (event) => {
        event.preventDefault();
        geocodeInput.value = '';
        geoClearButton.classList.add('hidden');
      });

      geocodeInput.addEventListener('input', () => {
        if (geocodeInput.value.length > 0) {
          geoClearButton.classList.remove('hidden');
        } else {
          geoClearButton.classList.add('hidden');
        }
      });

      // autocomplete panel section
      // aaPanel, ul
      aaPanel.setAttribute(
        'class',
        'aa-Panel search-autocomplete-panel z-50 w-full hidden'
      );

      aaForm.setAttribute('class', 'lg:flex gap-4 items-start');

      aaLoadingIndicator.setAttribute('class', 'aa-LoadingIndicator');
      aaLoadingIcon.setAttribute('class', 'aa-LoadingIcon');

      // prefix section
      // aaInput, aaLabel, aaClearButton
      aaExtendedSearchbox.setAttribute(
        'class',
        'aa-ExtendedSearchbox flex items-center relative'
      );
      aaClearButton.setAttribute('class', 'hidden group absolute right-3');
      aaClearButton.innerHTML = `
          <svg width="16" height="15" viewBox="0 0 16 15" class="w-4 h-4" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path class="fill-stone-400 group-hover:fill-current" d="M0.304688 7.5C0.304688 3.35742 3.66211 0 7.80469 0C11.9473 0 15.3047 3.35742 15.3047 7.5C15.3047 11.6426 11.9473 15 7.80469 15C3.66211 15 0.304688 11.6426 0.304688 7.5ZM5.43164 6.09668L6.81152 7.47363L5.43164 8.87695C5.15918 9.15234 5.15918 9.59766 5.43164 9.84668C5.70703 10.1455 6.15234 10.1455 6.40137 9.84668L7.77832 8.49316L9.18164 9.84668C9.45703 10.1455 9.90234 10.1455 10.1514 9.84668C10.4502 9.59766 10.4502 9.15234 10.1514 8.87695L8.79785 7.47363L10.1514 6.09668C10.4502 5.84766 10.4502 5.40234 10.1514 5.12695C9.90234 4.85449 9.45703 4.85449 9.18164 5.12695L7.77832 6.50684L6.40137 5.12695C6.15234 4.85449 5.70703 4.85449 5.43164 5.12695C5.15918 5.40234 5.15918 5.84766 5.43164 6.09668Z"/>
          </svg>`;

      aaInputWrapperPrefix.setAttribute(
        'class',
        'aaInputWrapperPrefix flex-1 relative'
      );

      aaLabel.setAttribute('for', 'urmcQuery');
      aaLabel.setAttribute('class', 'mb-3 font-semibold inline-block');
      aaLabel.innerHTML = `Treatment, Condition, or Provider Name`;

      aaInput.setAttribute(
        'class',
        'border rounded w-full p-3 leading-4 border-stone-400'
      );
      aaInput.setAttribute('id', 'urmcQuery');
      aaInput.setAttribute('name', 'urmcQuery');

      aaInput.setAttribute('type', 'text');
      aaInput.setAttribute('autocomplete', 'off');

      // wrapper section
      // geocodeInput, geocodeLabel, currentLocationButton, geoclearbutton
      aaExtendedGeobox.setAttribute(
        'class',
        'aa-ExtendedGeobox flex items-center relative'
      );
      geoClearButton.setAttribute(
        'class',
        '-ml-7 hidden group absolute right-3'
      );

      geoClearButton.innerHTML = `
        <svg width="16" height="15" viewBox="0 0 16 15" class="w-4 h-4" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path class="fill-stone-400 group-hover:fill-current" d="M0.304688 7.5C0.304688 3.35742 3.66211 0 7.80469 0C11.9473 0 15.3047 3.35742 15.3047 7.5C15.3047 11.6426 11.9473 15 7.80469 15C3.66211 15 0.304688 11.6426 0.304688 7.5ZM5.43164 6.09668L6.81152 7.47363L5.43164 8.87695C5.15918 9.15234 5.15918 9.59766 5.43164 9.84668C5.70703 10.1455 6.15234 10.1455 6.40137 9.84668L7.77832 8.49316L9.18164 9.84668C9.45703 10.1455 9.90234 10.1455 10.1514 9.84668C10.4502 9.59766 10.4502 9.15234 10.1514 8.87695L8.79785 7.47363L10.1514 6.09668C10.4502 5.84766 10.4502 5.40234 10.1514 5.12695C9.90234 4.85449 9.45703 4.85449 9.18164 5.12695L7.77832 6.50684L6.40137 5.12695C6.15234 4.85449 5.70703 4.85449 5.43164 5.12695C5.15918 5.40234 5.15918 5.84766 5.43164 6.09668Z"/>
        </svg>`;

      geocodeInput.setAttribute(
        'class',
        'w-full rounded border border-stone-400 p-3 leading-snug'
      );
      geocodeInput.setAttribute('id', 'geocodeInput');
      geocodeInput.setAttribute('type', 'text');
      geocodeInput.setAttribute('autocomplete', 'off');

      geocodeLabel.setAttribute('for', 'geocodeInput');
      geocodeLabel.setAttribute(
        'class',
        'mb-3 mt-6 font-semibold block lg:mt-0'
      );
      geocodeLabel.innerHTML = 'City or Zip Code';
      currentLocationButton.setAttribute('type', 'button');
      currentLocationButton.setAttribute('class', 'group-hover:underline');
      currentLocationButton.innerHTML = 'Use current location';

      iconContainer.setAttribute('class', 'w-3.5 h-3.5 mr-2');
      iconContainer.innerHTML = `
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              class="w-full h-full"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                class="fill-current"
                d="M7.34516 11.4582C7.18715 11.8451 6.77848 12.0631 6.36982 11.9841C5.96115 11.9023 5.6424 11.5454 5.6424 11.1286V6.3336H0.84741C0.455365 6.3336 0.0968307 6.03936 0.0164601 5.63069C-0.0639104 5.22203 0.156223 4.81337 0.541729 4.65535L10.1322 0.731634C10.4565 0.598682 10.8297 0.673876 11.0776 0.922071C11.3255 1.17054 11.4018 1.54378 11.2683 1.86881L7.34516 11.4582Z"
              />
            </svg>`;
      currentLocationContainer.setAttribute(
        'class',
        'flex items-center text-pacific-blue group hover:text-navy-blue cursor-pointer mt-2'
      );

      // suffix section
      // aaSubmitButton
      const spacer = document.createElement('div');
      spacer.setAttribute('class', 'search-btn-spacer');
      aaSubmitButton.setAttribute(
        'class',
        'btn btn-primary my-4 w-full max-w-none lg:my-0'
      );
      aaSubmitButton.innerHTML = 'Search';

      aaForm.onsubmit = (event) => {
        event.preventDefault();
        const query = aaInput.value;
        const zipcode = geocodeInput.value.length > 0 ? geocodeInput.value : '';

        const url = generateSearchResultsPath(query, zipcode).toString();

        window.location.href = url;
      };

      currentLocationButton.addEventListener('click', (e) => {
        e.preventDefault();
        getUserLatLng();
        geoClearButton.classList.remove('hidden');
      });

      if (
        typeof widgetParams.container === 'object' &&
        widgetParams.container !== null &&
        'appendChild' in widgetParams.container
      ) {
        widgetParams.container.appendChild(aaForm);
        widgetParams.container.appendChild(aaLoadingIndicator);

        // aaForm section
        aaForm.appendChild(aaInputWrapperPrefix);
        aaForm.appendChild(aaInputWrapper);
        aaForm.appendChild(aaInputWrapperSuffix);

        // aaExtendedSearchbox
        aaExtendedSearchbox.appendChild(aaInput);
        aaExtendedSearchbox.appendChild(aaClearButton);

        // prefix
        aaInputWrapperPrefix.appendChild(aaLabel);
        aaInputWrapperPrefix.appendChild(aaExtendedSearchbox);
        aaInputWrapperPrefix.appendChild(aaPanel);

        // aaExtendedGeobox
        aaExtendedGeobox.appendChild(geocodeInput);
        aaExtendedGeobox.appendChild(geoClearButton);

        // currentLocationContainer
        currentLocationContainer.appendChild(iconContainer);
        currentLocationContainer.appendChild(currentLocationButton);

        // wrapper
        aaInputWrapper.appendChild(geocodeLabel);
        aaInputWrapper.appendChild(aaExtendedGeobox);
        aaInputWrapper.appendChild(currentLocationContainer);

        // suffix
        aaInputWrapperSuffix.appendChild(spacer);
        aaInputWrapperSuffix.appendChild(aaSubmitButton);
      }

      // end of isFirstRender conditional
    }
  };

  // utils:

  // function to turn lat/lng into a zipcode
  function reverseGeocode() {
    geocoder
      .geocode({
        location: userLocation,
      })
      .then((response) => {
        const result = response.results[0];
        const addressComponentsArray = result?.address_components;

        const zipComponent = addressComponentsArray?.find((component) =>
          component.types.includes('postal_code')
        );
        const zipcode = zipComponent?.short_name as string;

        if (zipcode) {
          const geocodeInput = document.querySelector(
            '#geocodeInput'
          ) as HTMLInputElement;

          geocodeInput.value = zipcode;
          geocodeInput.focus();
        } else {
          window.alert('No results found');
        }
      })
      .catch((e) => window.alert('Geocoder failed due to: ' + e));
  }

  function getUserLatLng() {
    const options = {
      enableHighAccuracy: true,
      timeout: 5000,
      maximumAge: 0,
    };

    function success(pos: { coords: any }) {
      const crd = pos.coords;

      userLocation = {
        lat: crd.latitude,
        lng: crd.longitude,
      };
      reverseGeocode();
    }

    function error(err: { code: number; message: string }) {
      console.warn(`ERROR(${err.code}): ${err.message}`);
    }

    navigator.geolocation.getCurrentPosition(success, error, options);
  }

  const virtualSearchBox = connectSearchBox(() => null);
  const customAutocomplete = connectAutocomplete(renderAutocomplete);

  search.addWidgets([
    configure({
      filters: 'IsActiveClinician:true',
    }),
    customAutocomplete({
      container: document.querySelector('[data-app-search-box]'),
    }),

    // autocomplete can be styled with CSS custom properties
    // https://www.algolia.com/doc/ui-libraries/autocomplete/api-reference/autocomplete-theme-classic/
    virtualSearchBox({}),
  ]);

  search.start();

  // error handler to prevent uncaught errors
  search.on('error', () => {
    console.log('error', search.error);
  });

  search.on('render', () => {
    console.log('search status:', search.status);
    console.log('error', search.status === 'error' && search.error);
  });
}

type ProviderSearchResult = BaseItem & {
  Title: string;
  Url: string;
};
type SuggestionResult = BaseItem & AutocompleteQuerySuggestionsHit;
